// 오라(캐릭터 주변 위로 흐르는 고리·불꽃 띠): 대상을 감싸는 얇은 원통. 아래에서 위로 흐르는 불꽃 혀(노이즈가 위로 갈수록 끊김)와
// 위로 올라가는 고리 띠가 겹치고, 반지름이 느리게 숨 쉰다. 대상을 따라다닌다: 슬롯이 대상 핸들(FxTarget)을 들고 있어 매 프레임 그 위치로 옮겨 그린다.
// 사용: aura.follow(target) 뒤 spawn → 그 오라가 target을 따라다닌다(follow(null)로 해제, 이미 뜬 오라는 그대로 추적). target.active = false가 되면 0.2초 안에 사라진다.
//       target 없이 spawn하면 x,y,z에 고정된다.
// 쓰는 칸: length = 높이(m), width = 반지름(m), y = 대상 위치 기준 높이 오프셋(m), life = 최대 유지 시간(초; 대상이 사라지면 더 일찍 끝난다),
//          color(불꽃)·color2(고리·불꽃 끝)·intensity·seed. 드로우콜 1.
// 모양: 둘레 18칸 × 높이 4줄 원통 한 겹(위로 갈수록 65%로 가늘어짐). Blender 메시로 바꿀 때: u = 둘레 0..1, v = 아래 0 → 위 1.
import * as THREE from 'three/webgpu';
import { abs, clamp, cos, dot, float, fract, mix, sin, smoothstep, texture, uniform, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { gridLayout } from './layout';
import { fxMaterial } from './material';
import { clamp01 } from './path';
import { MODULE_CAPS, type FxTarget, type ModuleKind } from './types';

export const AURA_COLS = 18;
const ROW_V = [0, 0.35, 0.7, 1] as const;
const ROW_R = [0.95, 1.0, 0.88, 0.65] as const;
const SIN = new Float32Array(AURA_COLS + 1);
const COS = new Float32Array(AURA_COLS + 1);
for (let i = 0; i <= AURA_COLS; i++) {
  const a = (i / AURA_COLS) * Math.PI * 2;
  SIN[i] = Math.sin(a);
  COS[i] = Math.cos(a);
}
/** 대상이 사라진 뒤 오라가 꺼지는 시간(초) */
export const AURA_RELEASE = 0.2;

/** 수평 시선 방향(x, z) 유니폼 — 원통 둘레 중 시선과 나란히 스치는 옆면(실루엣)을 흐리게 하는 기준 */
function makeViewUniform() { return uniform(new THREE.Vector2(0, -1)); }
type ViewUniform = ReturnType<typeof makeViewUniform>;

function makeMaterial(noise: THREE.Texture, view: ViewUniform): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const env = ctl.y, seed = ctl.z;
  const t = t2.w;
  const u = uv().x, v = uv().y;
  // 불꽃 혀: 위로 흐르는 두 겹 노이즈. 위로 갈수록 문턱이 올라가 끊긴다
  const n = texture(noise, vec2(u.mul(3), v.mul(0.55).sub(t.mul(1.5)).add(seed))).g;
  const n2 = texture(noise, vec2(u.mul(5).add(0.3), v.mul(0.9).sub(t.mul(2.3)))).r;
  const m = n.mul(0.6).add(n2.mul(0.5));
  const thr = v.mul(0.95).add(0.05);
  const tongue = smoothstep(thr.sub(0.06), thr.add(0.1), m);
  // 올라가는 고리: 앞쪽은 날카롭고 뒤로 흐려진다
  const ph = fract(v.mul(2.2).sub(t.mul(1.1)));
  const ring = smoothstep(0.0, 0.08, ph).mul(float(1).sub(smoothstep(0.08, 0.3, ph)));
  // c013: 아래·위 끝을 둥글게 흐린다(딱 잘린 원통 윗선·밑선이 보이지 않게)
  const vIn = smoothstep(0.0, 0.22, v).mul(smoothstep(1.0, 0.7, v));
  // c013 가장자리 흐림(프레넬 반대): 둘레 각 u에서 면 법선(sin, cos)이 시선과 이루는 정도. 시선을 스치는 옆면(실루엣)은 0으로 흐린다.
  // 투명 원통은 옆면에서 겹쳐 보이는 면적이 커져 밝은 세로 윤곽(원통 실루엣)이 생겼다
  const a = u.mul(Math.PI * 2);
  const facing = abs(dot(vec2(sin(a), cos(a)), view));
  const rim = smoothstep(0.12, 0.7, facing);
  const alpha = clamp(tongue.mul(0.42).add(ring.mul(0.26)), 0, 1).mul(vIn).mul(rim).mul(env);
  const hot = clamp(v.mul(0.25).add(ring.mul(0.35)).add(n2.mul(0.15)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w).mul(0.6);
  return fxMaterial(col, alpha);
}

export class AuraModule extends BatchModule {
  readonly kind: ModuleKind = 'aura';
  /** 다음 spawn이 따라다닐 대상(없으면 null) */
  private nextTarget: FxTarget | null = null;
  /** 수평 시선 방향(정규화). 기본 = 남쪽에서 북쪽을 보는 전투 카메라 */
  private readonly view: ViewUniform;

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.aura) {
    const view = makeViewUniform();
    super(cap, gridLayout(AURA_COLS, ROW_V), makeMaterial(noise, view), 24, 'fx-aura');
    this.view = view;
  }

  /** 카메라 시선의 수평 성분(x, z). 거의 수직으로 내려보면 바꾸지 않는다 */
  setView(x: number, z: number): void {
    const l = Math.hypot(x, z);
    if (l < 1e-3) return;
    this.view.value.set(x / l, z / l);
  }

  /** 이 호출 뒤에 spawn하는 오라가 target을 따라다닌다. null이면 위치 고정 */
  follow(target: FxTarget | null): void { this.nextTarget = target; }

  protected override onSpawn(s: Slot): void { s.tgt = this.nextTarget; }

  protected override done(s: Slot): boolean {
    const t = s.tgt;
    if (t !== null && !t.active && s.rel < 0) s.rel = s.age;
    if (s.rel >= 0 && s.age - s.rel >= AURA_RELEASE) return true;
    return s.age >= s.life;
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const t = s.tgt;
    const cx = t !== null ? t.x : s.x;
    const cz = t !== null ? t.z : s.z;
    const base = (t !== null ? t.y : 0) + s.y;
    const H = s.length * s.scale;
    // 숨 쉬는 반지름(약 1.2Hz, ±4%)
    const R = s.width * s.scale * (1 + 0.04 * Math.sin(s.age * 7.5 + s.seed * 6.283));
    let k = o;
    for (let j = 0; j < ROW_V.length; j++) {
      const y = base + H * ROW_V[j];
      const rr = R * ROW_R[j];
      for (let i = 0; i <= AURA_COLS; i++) {
        out[k] = cx + SIN[i] * rr; out[k + 1] = y; out[k + 2] = cz + COS[i] * rr;
        k += 3;
      }
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    let e = Math.min(1, s.age / 0.15) * (1 - clamp01((age01 - 0.9) / 0.1));
    if (s.rel >= 0) e *= 1 - clamp01((s.age - s.rel) / AURA_RELEASE);
    out[1] = e;
    out[2] = s.seed;
    out[3] = 0;
  }

  protected override tint2w(s: Slot): number { return s.age; }
}
